import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_ops_assistant_confirm_action,
} from "../../../constants";
import { toast } from "react-toastify";
import formatDate from "./formatDate";

export default function ActionCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
  confirmUrl = API_ops_assistant_confirm_action,
  scopeParams,
}) {
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loadingAction, setLoadingAction] = useState(null); // "confirm" or "reject"
  // Executor result after a confirm; some actions report a partial outcome
  // (e.g. an email that could not be sent) that the user must see.
  const [executionResult, setExecutionResult] = useState(null);

  // Sync with parent when proposal is externally cancelled
  useEffect(() => {
    if ((proposal.status === "cancelled" || proposal.status === "rejected") && status === "pending") {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  const summary = proposal.summary || {};

  const handleAction = async (action) => {
    setLoadingAction(action);
    try {
      const res = await postRightworkAPI(confirmUrl, {
        ...(scopeParams || { store_id: homeStore }),
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action: action,
      });
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        const payloadResult = res?.data?.redux?.payload?.result;
        setExecutionResult(payloadResult && typeof payloadResult === "object" ? payloadResult : null);
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoadingAction(null);
  };

  // Collapsed confirmed state: green when the action fully succeeded, amber
  // when the executor reported a follow-up the user must handle (its note and,
  // if delivery failed, the fallback link to share manually).
  if (status === "confirmed") {
    const followUpNote = executionResult?.note;
    const fallbackLink = executionResult?.magic_link;
    const needsFollowUp = Boolean(followUpNote || fallbackLink);
    return (
      <div
        className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm my-2 border ${
          needsFollowUp
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-green-50 border-green-200 text-green-700"
        }`}
      >
        <CheckCircleIcon
          fontSize="small"
          className={`mt-0.5 shrink-0 ${needsFollowUp ? "text-amber-600" : "text-green-600"}`}
        />
        <div className="min-w-0">
          <div className="font-medium">{summary.title || "Done"}</div>
          {summary.employee_name && (
            <div className="text-xs opacity-80">{summary.employee_name}</div>
          )}
          {summary.dates && (
            <div className="text-xs opacity-70">{formatDate(summary.dates)}</div>
          )}
          {executionResult?.detail && (
            <div className="text-xs mt-1 flex items-center gap-1.5">
              {executionResult?.color && (
                <span
                  data-testid="action-result-color"
                  aria-label={`Color ${executionResult.color}`}
                  className="inline-block w-3 h-3 rounded-full border border-black/10 shrink-0"
                  style={{ backgroundColor: executionResult.color }}
                />
              )}
              <span>{executionResult.detail}</span>
            </div>
          )}
          {followUpNote && <div className="text-xs mt-1">{followUpNote}</div>}
          {fallbackLink && (
            <a
              href={fallbackLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs mt-1 underline break-all select-all"
            >
              {fallbackLink}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Action cancelled</span>
      </div>
    );
  }

  // Pending state — full card
  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <HourglassEmptyIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          {summary.title || "Pending Action"}
        </span>
      </div>

      {/* Content */}
      <div className="px-3 py-2 space-y-1">
        {summary.employee_name && (
          <div className="text-sm">
            <span className="text-gray-500">Employee: </span>
            <span className="font-medium text-gray-700">{summary.employee_name}</span>
          </div>
        )}
        {summary.type && (
          <div className="text-sm">
            <span className="text-gray-500">Type: </span>
            <span className="font-medium text-gray-700">{summary.type}</span>
          </div>
        )}
        {summary.dates && (
          <div className="text-sm">
            <span className="text-gray-500">Dates: </span>
            <span className="font-medium text-gray-700">{formatDate(summary.dates)}</span>
          </div>
        )}
        {summary.date && !summary.dates && (
          <div className="text-sm">
            <span className="text-gray-500">Date: </span>
            <span className="font-medium text-gray-700">{formatDate(summary.date)}</span>
          </div>
        )}
        {summary.from_employee && (
          <div className="text-sm">
            <span className="text-gray-500">From: </span>
            <span className="font-medium text-gray-700">{summary.from_employee}</span>
            <span className="text-gray-500"> → </span>
            <span className="font-medium text-gray-700">{summary.to_employee}</span>
          </div>
        )}
        {summary.count && (
          <div className="text-sm">
            <span className="text-gray-500">Count: </span>
            <span className="font-medium text-gray-700">{summary.count}</span>
          </div>
        )}
        {summary.note && (
          <div className="text-sm text-gray-600 bg-white rounded p-2 mt-1">
            {summary.note}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={() => handleAction("reject")}
          disabled={!!loadingAction}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1"
        >
          {loadingAction === "reject" && <CircularProgress size={14} sx={{ color: "#9ca3af" }} />}
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={!!loadingAction}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loadingAction === "confirm" && <CircularProgress size={14} sx={{ color: "white" }} />}
          Confirm
        </button>
      </div>
    </div>
  );
}
